import { useEffect, useState, useMemo, useCallback } from 'react';
import { useResponsiveLayout } from './useResponsiveLayout';
import { useOrganizationContext } from '../service/context/organizationContext';
import { useUserDetailContext } from '../service/context/userDetailContext';
import {
  GetHarmReportsResponse,
  HarmReportsFilter,
  HarmReportsKebabOption,
  HarmReportsPendingAction,
} from '../pages/harmReports/harmReports.type';
import { ReportService } from '../service/reportService';
import {
  GetReportsByFilterRequest,
  HarmReport,
  ReportStatus,
} from '@inplay/models';
import { toastV2 } from '../component/toastV2/toastV2';
import { useTranslation } from 'react-i18next';
import { validateReportsForDeletion } from '../pages/harmReports/harmReportesHelper';
import { useNavigate } from 'react-router-dom';
import { SelectedUser } from '../component/userVerificationModal/userVerificationModal.types';
import { route } from '../service/router';
import { getErrorMessage } from '../utils/helper';

export const useHarmReports = () => {
  const navigate = useNavigate();
  const { selectedVenueBranch, loading: organizationContextLoading } =
    useOrganizationContext();
  const { userDetail } = useUserDetailContext();
  const { isTablet } = useResponsiveLayout();
  const { t } = useTranslation();

  const [allHarmReports, setAllHarmReports] = useState<HarmReport[]>([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState<HarmReportsFilter>({
    searchQuery: '',
    selectedDate: new Date(),
    status: [ReportStatus.ACTIVE],
  });

  const [selectedRows, setSelectedRows] = useState<HarmReport[]>([]);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [openCreateHarm, setOpenCreateHarm] = useState(false);
  const [isReviewAndEditMode, setIsReviewAndEditMode] = useState(false);

  const reportService = useMemo(() => new ReportService(), []);
  const [selectedHarmReport, setSelectedHarmReport] =
    useState<HarmReport | null>(null);
  const handleRowClick = (row: HarmReport) => {
    setSelectedHarmReport(row);
  };
  const [harmReportForCreate, setHarmReportForCreate] =
    useState<HarmReport | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUserVerification, setShowUserVerification] =
    useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [verifiedUser, setVerifiedUser] = useState<SelectedUser | null>(null);
  const [showSelectPlayerModal, setShowSelectPlayerModal] =
    useState<boolean>(false);

  const handleOnVerify = (user: SelectedUser) => {
    setVerifiedUser(user);
    setShowUserVerification(false);

    switch (pendingAction) {
      case HarmReportsPendingAction.CREATE_HARM_REPORT:
        setShowSelectPlayerModal(true);
        break;
      case HarmReportsPendingAction.DELETE_REPORTS:
        setShowDeleteConfirmation(true);
        setPendingAction(null);
        break;
      case HarmReportsPendingAction.EDIT_REPORT:
        setOpenCreateHarm(true);
        setPendingAction(null);
        break;
      default:
        break;
    }
  };

  const handleOnProceed = () => {
    setShowSelectPlayerModal(false);
    switch (pendingAction) {
      case HarmReportsPendingAction.CREATE_HARM_REPORT:
        setOpenCreateHarm(true);
        setPendingAction(null);
        break;
      default:
        break;
    }
  };

  const closeHarmReportDetails = () => {
    setSelectedHarmReport(null);
  };

  const fetchHarmReports = useCallback(
    async (override?: Partial<HarmReportsFilter>) => {
      if (!selectedVenueBranch) return;
      const filtersToUse = { ...filters, ...override };
      setLoading(true);
      try {
        const query: GetReportsByFilterRequest = {
          venueBranchId: selectedVenueBranch._id,
          currentDate: filtersToUse.selectedDate,
          status: filtersToUse.status,
        };
        const response: GetHarmReportsResponse =
          await reportService.getHarmReports(query);
        const harmReports: HarmReport[] = response.data?.harmReports ?? [];
        setAllHarmReports(harmReports);
      } catch (error) {
        console.error('Error fetching harm reports:', error);
        toastV2.error('Failed to fetch harm reports.');
      } finally {
        setLoading(false);
      }
    },
    [selectedVenueBranch],
  );

  useEffect(() => {
    fetchHarmReports();
  }, [selectedVenueBranch]);

  const harmReports: HarmReport[] = useMemo(() => {
    const searchQuery = filters.searchQuery?.toLowerCase();

    if (!searchQuery) return allHarmReports;

    return allHarmReports.filter(
      (report) =>
        report.reportRefNo?.toLowerCase().includes(searchQuery) ||
        report.playerProfile.id?.toLowerCase().includes(searchQuery) ||
        report.createdBy?.firstName?.toLowerCase().includes(searchQuery) ||
        report.createdBy?.lastName?.toLowerCase().includes(searchQuery) ||
        report.playerDetails.description?.toLowerCase().includes(searchQuery),
    );
  }, [allHarmReports, filters.searchQuery]);

  const handleOnChangeSearch = (query: string) => {
    setFilters((f) => ({ ...f, searchQuery: query }));
  };

  const handleClearFilters = () => {
    const reset = {
      searchQuery: '',
      selectedDate: new Date(),
    };
    setFilters(reset);
    fetchHarmReports(reset);
  };

  const handleApplyFilters = (newFilters: Partial<HarmReportsFilter>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    fetchHarmReports(newFilters);
  };

  const handleDateApply = (date: Date) => {
    const normalizedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    console.log('Selected date:', {
      original: date,
      normalized: normalizedDate,
    });
    setFilters((prev) => ({ ...prev, selectedDate: normalizedDate }));
    fetchHarmReports({ selectedDate: normalizedDate });
  };

  const handleReportHarmClick = () => {
    setPendingAction(HarmReportsPendingAction.CREATE_HARM_REPORT);
    setShowUserVerification(true);
  };

  const handleReportHarmClose = () => {
    setOpenCreateHarm(false);
    setVerifiedUser(null);
    setHarmReportForCreate(null);
  };

  const toggleFilterModal = () => setOpenFilterModal((v) => !v);

  const handleSelectionChange = (rows: HarmReport[]) => {
    setSelectedRows(rows);
  };

  const handleDeleteHarmReports = async () => {
    try {
      if (!selectedRows.length) return;

      const { valid, invalid } = validateReportsForDeletion(
        selectedRows,
        userDetail,
      );

      if (invalid.length > 0) {
        toastV2.error(invalid[0]); // Show first validation error
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      if (!valid.length) {
        toastV2.error('No valid reports selected for deletion.');
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      const originalReportIds = valid
        .map((row) => row.originalReportId)
        .filter((id): id is string => Boolean(id));

      if (!originalReportIds.length) {
        toastV2.error('No valid report IDs found.');
        setShowDeleteConfirmation(false);
        setSelectedRows([]);
        return;
      }

      setDeleting(true);
      const response = await reportService.deleteHarmReports(originalReportIds);
      const count = response.deletedHarmReportsCount;

      toastV2.success(
        `Successfully deleted ${count} harm report${count === 1 ? '' : 's'}`,
      );

      setSelectedRows([]);
      setShowDeleteConfirmation(false);
      await fetchHarmReports(filters);
    } catch (error: unknown) {
      const errorMessage: string = getErrorMessage(error);
      console.error(errorMessage);
      toastV2.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    setPendingAction(HarmReportsPendingAction.DELETE_REPORTS);
    setShowUserVerification(true);
  };

  const handleKebabActionFromKebabMenu = async (
    action: HarmReportsKebabOption,
    row: HarmReport,
    rowIndex: number,
  ): Promise<void> => {
    console.log('[useHarmReports][handleKebabActionFromKebabMenu] ENTRY', {
      action,
      row,
      rowIndex,
    });
    switch (action) {
      case HarmReportsKebabOption.VIEW:
        setSelectedHarmReport(row);
        return;
      case HarmReportsKebabOption.EDIT:
        setHarmReportForCreate(row);
        setPendingAction(HarmReportsPendingAction.EDIT_REPORT);
        setShowUserVerification(true);
        setIsReviewAndEditMode(false);
        return;
      case HarmReportsKebabOption.REVIEW_AND_SIGN:
        setHarmReportForCreate(row);
        setOpenCreateHarm(true);
        setIsReviewAndEditMode(true);
        return;
      case HarmReportsKebabOption.DOWNLOAD:
      case HarmReportsKebabOption.HISTORY:
        toastV2.warning('Feature not yet available');
        return;
      case HarmReportsKebabOption.DELETE:
        setSelectedRows([row]);
        setPendingAction(HarmReportsPendingAction.DELETE_REPORTS);
        setShowUserVerification(true);
        return;
      default: {
        console.error('Invalid Harm Report Kebab Option');
        const _exhaustiveCheck: never = action;
        return _exhaustiveCheck;
      }
    }
  };

  const handleSettingMenuSelect = (label: string | undefined) => {
    console.log({ label });
    if (!label) return;
    if (label === 'harmReports.settingOption.download') {
      toastV2.info(t('common.functionalityComingSoon'));
    } else if (label === 'harmReports.settingOption.deletedReports') {
      navigate(route.deletedHarmReports);
    }
  };

  const handleReviewAndSignClick = () => {
    if (!selectedHarmReport) return;
    setHarmReportForCreate(selectedHarmReport);
    setOpenCreateHarm(true);
    setIsReviewAndEditMode(true);
    setSelectedHarmReport(null);
  };

  return {
    harmReports,
    loading,
    filters,
    selectedRows,
    setSelectedRows,
    openFilterModal,
    openCreateHarm,
    isReviewAndEditMode,
    isTablet,
    selectedVenueBranch,
    fetchHarmReports,
    handleOnChangeSearch,
    handleApplyFilters,
    handleClearFilters,
    handleDateApply,
    handleSelectionChange,
    handleReportHarmClick,
    handleReportHarmClose,
    handleKebabActionFromKebabMenu,
    handleDeleteHarmReports,
    toggleFilterModal,
    selectedHarmReport,
    handleRowClick,
    closeHarmReportDetails,
    handleReviewAndSignClick,
    organizationContextLoading,
    harmReportForCreate,
    t,
    handleDeleteClick,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleting,
    showUserVerification,
    setShowUserVerification,
    handleOnVerify,
    handleOnProceed,
    setShowSelectPlayerModal,
    showSelectPlayerModal,
    handleSettingMenuSelect,
    verifiedUser,
  };
};
